import axios from 'axios';
import { parseError } from '../utils/errorUtils';
import type { DivisionScheme } from '../schemaForm/fields/divisionScheme';
import type { GroupSchedulePreview, GroupMemberChangeResult, SchedulePreview, ScheduleAction, ScheduleCharge } from '../types/schedulePrompt';

// =============================================================================
// RETRY CONFIGURATION
// =============================================================================

const MAX_RETRIES = 3;
const RETRY_DELAY_BASE = 1000; // 1 second

/**
 * Extended axios config with retry tracking
 * Using manual type definition for compatibility with all axios versions
 */
interface RetryableAxiosRequestConfig {
  __retryCount?: number;
  __isRetry?: boolean;
  url?: string;
  method?: string;
  headers?: Record<string, string>;
  data?: unknown;
  params?: unknown;
  timeout?: number;
  baseURL?: string;
  [key: string]: unknown;
}

/**
 * Axios error structure for type checking
 */
interface AxiosErrorLike {
  isAxiosError?: boolean;
  code?: string;
  config?: RetryableAxiosRequestConfig;
  request?: unknown;
  response?: {
    status: number;
    data?: unknown;
  };
}

/** HTTP methods that are safe to replay blind. Everything else may have been
 *  committed by the server with only the response lost. */
const SAFE_METHODS = new Set(['get', 'head', 'options']);

/**
 * Determine if an error should trigger a retry.
 *
 * Network errors only, and only for SAFE methods. A network error on a write is
 * exactly the case where the server may already have committed — replaying it
 * blind refunds four times, double-runs a course renewal, generates events
 * twice. Without an idempotency key the client cannot tell, so writes surface
 * the failure to the caller instead.
 */
export const shouldRetry = (error: AxiosErrorLike): boolean => {
  // Got a response - don't retry (this is an HTTP error like 4xx/5xx)
  if (error.response) return false;

  const method = (error.config?.method ?? 'get').toLowerCase();
  if (!SAFE_METHODS.has(method)) return false;

  // Check for retryable error codes
  if (error.code === 'ECONNABORTED') return true; // Timeout
  if (error.code === 'ERR_NETWORK') return true; // Network error
  if (error.code === 'ETIMEDOUT') return true; // Timeout
  if (error.code === 'ECONNREFUSED') return true; // Connection refused
  if (error.code === 'ECONNRESET') return true; // Connection reset

  return false;
};

/**
 * Calculate retry delay with exponential backoff
 * @param retryCount - Current retry attempt (1-indexed)
 * @returns Delay in milliseconds
 */
const getRetryDelay = (retryCount: number): number => {
  return RETRY_DELAY_BASE * Math.pow(2, retryCount - 1);
};

// =============================================================================
// TENANT EXTRACTION
// =============================================================================

/**
 * Extract tenant slug from current hostname
 * Examples:
 * - "summer-camp.eventik.cz" -> "summer-camp"
 * - "eventik.cz" -> null (main domain)
 * - "summer-camp.eventik.up.railway.app" -> "summer-camp"
 * - "eventik.up.railway.app" -> null (main domain)
 * - "localhost:3000" -> null (no tenant, unless devTenantSlug set)
 */
/**
 * The figures printed under a payment QR, so a payer whose scan fails can enter
 * the payment by hand. Returned by the `*-qr-details` siblings of every QR
 * endpoint — same handler, so these always match the image.
 */
export interface PaymentQrDetails {
  accountNumber: string;
  variableSymbol: string;
  amount: number;
}

export const getTenantSlug = (): string | null => {
  const hostname = window.location.hostname;

  // Development: check for *.localhost subdomains (e.g., my-org.localhost:3000)
  if (hostname.endsWith('.localhost')) {
    const subdomain = hostname.replace('.localhost', '');
    if (subdomain && subdomain !== 'www' && subdomain !== 'api') {
      return subdomain;
    }
  }

  // Development: plain localhost - check localStorage override
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const devTenant = localStorage.getItem('devTenantSlug');
    return devTenant || null;
  }

  // Railway deployment: eventik.up.railway.app is main, *.eventik.up.railway.app is tenant
  if (hostname.endsWith('.eventik.up.railway.app')) {
    // Has subdomain before eventik.up.railway.app
    const subdomain = hostname.replace('.eventik.up.railway.app', '');
    if (subdomain && subdomain !== 'www' && subdomain !== 'api' && subdomain !== 'admin') {
      return subdomain;
    }
    return null;
  }

  // Main Railway domain without tenant
  if (hostname === 'eventik.up.railway.app') {
    return null;
  }

  // Production domain: eventik.cz is main, *.eventik.cz is tenant
  if (hostname.endsWith('.eventik.cz')) {
    const subdomain = hostname.replace('.eventik.cz', '');
    if (subdomain && subdomain !== 'www' && subdomain !== 'api' && subdomain !== 'admin') {
      return subdomain;
    }
    return null;
  }

  // Main production domain without tenant
  if (hostname === 'eventik.cz') {
    return null;
  }

  // Fallback for other domains: extract subdomain generically
  const parts = hostname.split('.');

  // Need at least 3 parts for subdomain (sub.domain.tld)
  if (parts.length < 3) {
    return null;
  }

  const subdomain = parts[0];

  // Ignore common non-tenant subdomains
  if (!subdomain || subdomain === 'www' || subdomain === 'api' || subdomain === 'admin') {
    return null;
  }

  return subdomain;
};

/**
 * Check if we're on the main domain (no tenant context)
 */
export const isMainDomain = (): boolean => {
  return getTenantSlug() === null;
};

/**
 * Get the base URL for API calls
 * Uses environment variable or defaults based on hostname
 */
export const getApiBaseUrl = (): string => {
  // Use environment variable if set
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }

  // Default based on environment
  const hostname = window.location.hostname;

  // Development: localhost or *.localhost subdomains
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.localhost')) {
    return 'http://localhost:5000';
  }

  // Production: API on same domain
  return `${window.location.protocol}//api.eventik.cz`;
};

// =============================================================================
// AUTH TOKEN MANAGEMENT
// =============================================================================

export interface OrganizationInfo {
  _id: string;
  name: string;
  slug: string;
}

export interface RoleInfo {
  _id: string;
  name: string;
  permissions: Record<string, number>;
}

export interface MembershipInfo {
  _id?: string;
  organization: OrganizationInfo;
  role: RoleInfo;
  joinedAt: string;
  isActive: boolean;
}

export interface BillingDetailsPayload {
  legalName: string;
  ico: string;
  dic?: string;
  address: { street: string; city: string; zip: string; country: string };
  billingEmail: string;
  note?: string;
}

/** A tenant user offered by the impersonation switcher. */
export interface ImpersonationUser {
  _id: number;
  globalUserId: number;
  name: string;
  email: string;
  role: { name: string; type: 'admin' | 'user' };
  /** Resolved server-side — tenant `users.id` is a different id space from the
   *  global account id the client holds, and the two must never be compared. */
  isCurrent: boolean;
}

export interface ImpersonationSwitchResponse {
  token: string;
  expiresIn: string;
  organization: OrganizationInfo;
  user: { _id: number; tenantUserId: number; name: string; email: string };
  role?: RoleInfo & { type?: 'admin' | 'user'; isSystem?: boolean };
}

export interface UserInfo {
  staffProfile?: { aboutMe: string; photoUrl: string | null };
  _id: string;
  name: string;
  email: string;
  phoneNumber?: string;
  token: string;
  memberships?: MembershipInfo[];
  currentMembership?: MembershipInfo | null;
  requiresOrgSelection?: boolean;
  preferences?: Record<string, unknown>;
  // Legacy fields for backwards compatibility
  organization?: OrganizationInfo;
  role?: RoleInfo;
  isSuperAdmin?: boolean;
}

/**
 * Get current user info from localStorage
 */
export const getUserInfo = (): UserInfo | null => {
  try {
    const stored = localStorage.getItem('userInfo');
    if (!stored) return null;
    return JSON.parse(stored);
  } catch {
    return null;
  }
};

/**
 * Current TENANT user id (the org-scoped `users.id`) — the id space that all
 * tenant data uses: `event.responsibleId`, `message.sender._id`, conversation
 * `participant._id`, etc. This is `currentMembership._id`, which the auth flow
 * sets to the tenant user id and keeps fresh across org switches.
 *
 * NOTE: `userInfo._id` is the GLOBAL account id (a different id space) — do NOT
 * compare it against tenant ids. Falls back to the global id only if no
 * membership is resolved (legacy/degenerate sessions).
 */
export const getCurrentTenantUserId = (): string => {
  const info = getUserInfo();
  return String(info?.currentMembership?._id ?? info?._id ?? '');
};

/**
 * Read the JSON error body of a failed `responseType: 'blob'` request.
 * Axios delivers error responses as a Blob too, so `err.response.data.message`
 * is undefined and the real `{ message, code }` is hidden inside the blob.
 */
export const readBlobError = async (
  err: any,
): Promise<{ message?: string; code?: string }> => {
  const data = err?.response?.data;
  try {
    if (data instanceof Blob) return JSON.parse(await data.text());
    if (data && typeof data === 'object') return data;
  } catch {
    /* not JSON */
  }
  return {};
};

/**
 * Save user info to localStorage
 */
export const setUserInfo = (userInfo: UserInfo): void => {
  localStorage.setItem('userInfo', JSON.stringify(userInfo));
};

/**
 * Clear user info (logout)
 */
export const clearUserInfo = (): void => {
  localStorage.removeItem('userInfo');
};

/**
 * Get auth token
 */
export const getAuthToken = (): string | null => {
  const userInfo = getUserInfo();
  return userInfo?.token || null;
};

// =============================================================================
// AXIOS INSTANCE
// =============================================================================

/**
 * Create configured axios instance
 */
const createApiClient = () => {
  const client = axios.create({
    baseURL: getApiBaseUrl(),
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor - add auth and tenant headers
  client.interceptors.request.use(
    (config) => {
      // Add auth token. An Authorization header set explicitly by the caller
      // wins — the org-switch hand-off authenticates with a token that is not
      // in localStorage yet (and may differ from a stale one that is).
      const token = getAuthToken();
      if (token && config.headers && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Add tenant slug header
      const tenantSlug = getTenantSlug();
      if (tenantSlug && config.headers) {
        config.headers['X-Tenant-Slug'] = tenantSlug;
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor - handle errors and retry logic
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      // Cast to our error type for type checking
      const axiosError = error as AxiosErrorLike;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const config = axiosError.config as any;

      // If no config, we can't retry
      if (!config) {
        return Promise.reject(error);
      }

      // Initialize retry count
      config.__retryCount = config.__retryCount || 0;

      // Check if should retry (network errors only)
      if (shouldRetry(axiosError) && config.__retryCount < MAX_RETRIES) {
        config.__retryCount += 1;
        config.__isRetry = true;

        // Calculate delay with exponential backoff
        const delay = getRetryDelay(config.__retryCount);

        // Log retry attempt for debugging
        console.debug(
          `[API] Retrying request (${config.__retryCount}/${MAX_RETRIES}) after ${delay}ms:`,
          config.url
        );

        // Wait then retry
        await new Promise((resolve) => setTimeout(resolve, delay));
        return client.request(config);
      }

      // Handle 401 Unauthorized - redirect to login
      if (axiosError.response?.status === 401) {
        clearUserInfo();

        // Don't redirect if already on login/register pages
        const path = window.location.pathname;
        if (!path.includes('/login') && !path.includes('/register') && !path.includes('/auth/callback')) {
          window.location.href = '/login';
        }
      }

      // Handle 403 Forbidden - permission denied
      if (axiosError.response?.status === 403) {
        const data = axiosError.response.data as { code?: string; message?: string };

        // Check for subscription-related errors
        if (data?.code === 'SUBSCRIPTION_EXPIRED') {
          console.warn('Subscription expired - read-only mode');
          window.dispatchEvent(new CustomEvent('subscription-expired'));
        }
      }

      // Handle 404 Organization not found - silently (components handle this)
      if (axiosError.response?.status === 404) {
        const data = axiosError.response.data as { code?: string };
        if (data?.code === 'ORG_NOT_FOUND') {
          // Don't log - let components handle this gracefully
          // Components can check err.response.data.code === 'ORG_NOT_FOUND'
        }
      }

      // Log timeout errors specifically
      if (axiosError.code === 'ECONNABORTED') {
        console.error('[API] Request timeout:', config?.url);
      }

      return Promise.reject(error);
    }
  );

  return client;
};

// Export singleton instance
export const apiClient = createApiClient();

// =============================================================================
// TYPED API HELPERS
// =============================================================================

/**
 * Generic API response type
 */
export interface ApiResponse<T> {
  data: T;
  status: number;
}

/**
 * Communication Center — unified thread abstraction.
 * A thread is keyed by (type, refId): event-comment threads use the eventId,
 * direct-message threads use the conversationId.
 */
export interface CommThread {
  type: 'event' | 'dm';
  refId: number;
  title: string;
  subtitle: string | null;
  lastActivityAt: string | null; // ISO
  snippet: string | null;
  unreadCount: number;
  meta: {
    startTime?: string;
    eventId?: number;
    participants?: Array<{ _id: number; name: string }>;
    groupId?: number;
  };
}

/**
 * Query parameters for the communication threads endpoint.
 */
export interface CommThreadsParams {
  type?: 'event' | 'dm';
  search?: string;
  eventId?: number;
  timeframe?: 'upcoming' | 'recent' | 'past';
  unreadOnly?: boolean;
  window?: number;
  limit?: number;
  offset?: number;
}

/**
 * Paginated response type
 */
export interface PaginatedResponse<T> {
  items: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

/**
 * Public pricing — usage-based model. A single base plan (the "199 plan")
 * includes a baseline allowance; capacity beyond that is granted via add-on
 * blocks. There are no fixed tiers.
 */
export interface PublicPricing {
  currency: string;
  basePrice: number;
  includedActivities: number;
  includedMembers: number;
  includedStorageMB: number;
  addons: {
    memberBlock: { size: number; price: number };
    activity: { price: number };
  };
}

/**
 * Per-dimension usage detail returned by GET /api/organization/usage.
 * `limit` of -1 means unlimited (don't render as capped).
 */
export interface UsageDetail {
  current: number;
  limit: number;
  percentUsed: number;
}

/**
 * Spend-ceiling configuration for an organization. `memberLimit` and
 * `activityLimit` are the org-set caps (the ceiling on what it will pay for);
 * `includedMembers`/`includedActivities` are the base allowance bundled in the
 * base price. `storageMB` is the granted storage ceiling.
 */
export interface OrganizationEntitlements {
  memberLimit: number;
  activityLimit: number;
  storageMB: number;
  includedMembers: number;
  includedActivities: number;
}

/**
 * Computed monthly cost from a usage-based bill. `cost` is the bill from
 * ACTUAL current usage; `costAtLimit` is the maximum bill at the chosen
 * ceiling. Tier names are intentionally absent — this is a single flexible plan.
 */
export interface UsageCost {
  currency: string;
  base: number;
  includedMembers: number;
  includedActivities: number;
  memberBlocks: number;
  memberBlockPrice: number;
  memberCost: number;
  extraActivities: number;
  activityAddonPrice: number;
  activityCost: number;
  total: number;
}

/**
 * Organization usage response. In addition to live `usage`, it carries the
 * `entitlements` (the org-set spend ceiling), the current `cost` from actual
 * usage, the `costAtLimit` (max bill at the ceiling) and the `pricing` model.
 */
export interface OrganizationUsage {
  plan: string;
  usage: {
    admins: UsageDetail;
    internalMembers: UsageDetail;
    storageMB: UsageDetail;
    activeOpenEvents: UsageDetail;
    activities: UsageDetail;
  };
  features: {
    customBranding: boolean;
  };
  entitlements: OrganizationEntitlements;
  cost: UsageCost;
  costAtLimit: UsageCost;
  pricing: {
    currency: string;
    basePrice: number;
    includedMembers: number;
    includedActivities: number;
    memberBlockSize: number;
    memberBlockPrice: number;
    activityAddonPrice: number;
  };
}

/**
 * A course's persisted membership OPTION — a `membership` row whose `courseId`
 * points at the course. Returned both from GET /api/courses/:id (as the
 * `memberships` array) and from the membership-option write endpoints.
 */
export interface ICourseMembershipOption {
  _id: number;
  name: string;
  description?: string;
  type: 'season' | 'calendar' | 'time' | 'entry';
  entryCount?: number | null;
  price?: string | number | null;
  /**
   * Raw pricing tier ladder (time options) as stored in `memberships.prices`.
   * Present on the course detail payload so the renewal modal can seed a full
   * per-option tier editor (labels / durations / default) rather than a flat
   * price. Null/absent for entry/calendar options.
   */
  prices?: Array<{
    durationDays: number;
    price: number | string;
    label: string;
    sortOrder: number;
    isDefault: boolean;
  }> | null;
  /** Raw calendar config (calendar options) — carried forward verbatim on renewal. */
  calendarConfig?: unknown;
  /**
   * Season-division split definition (fractions + per-segment prices) for
   * `season` options. Present so the course-edit Payments editor round-trips the
   * season-division shape through DivisionSchemeField. Null/absent for legacy types.
   */
  divisionScheme?: DivisionScheme | null;
  priceOptions?: Array<{
    amount: number;
    interval?: string;
    durationDays?: number;
    entryCount?: number;
  }>;
  courseId: number;
}

/**
 * Payload for creating / updating a course membership option. Mirrors the
 * membership write contract the route accepts (the course context is taken
 * from the URL, so `courseId` is not sent in the body).
 */
export interface ICourseMembershipOptionPayload {
  name: string;
  description?: string;
  type: 'season' | 'calendar' | 'time' | 'entry';
  yearlyPrice?: number;
  prices?: unknown[];
  entryCount?: number | null;
  /** Season-division split definition sent for `season` options. */
  divisionScheme?: DivisionScheme;
}

/**
 * Detail payload from GET /api/courses/:id. Carries the bounded term
 * (`termStart`/`termEnd`, ISO date strings or null) plus the derived
 * `isExpired` flag, and the course's membership options.
 */
/**
 * DELETE /api/event-templates/:id — sessions carrying attendance or money are
 * SPARED and fall out of the series (template_id -> NULL), so they need saying.
 */
export interface TemplateDeleteResponse {
  message: string;
  eventsDeleted: number;
  eventsKept: number;
  chargesCancelled: number;
}

/** DELETE /api/members/:id — what survived the soft delete. */
export interface MemberDeleteResponse {
  message: string;
  memberId: number;
  /** Present when the member was already a tombstone (cleanup re-run). */
  alreadyDeleted?: boolean;
  retained: { payments: number; attendanceRecords: number };
}

/** 409 from POST /api/members when a deleted member holds the birth number. */
export interface MemberDeletedRestorable {
  code: 'MEMBER_DELETED_RESTORABLE';
  message: string;
  deletedMember: { _id: number; name: string; deletedAt: string; retainedPayments: number };
  restoreUrl: string;
}

export interface ICourseDetail {
  _id: string | number;
  name: string;
  description?: string;
  activityId?: number | null;
  capacity?: number | null;
  enrollmentPolicy: 'open' | 'approval';
  isActive: boolean;
  instructorId?: number | null;
  coverImage?: string | null;
  gallery?: string[] | null;
  extendedDescription?: string | null;
  colorSettings?: { primaryColor?: string; secondaryColor?: string } | null;
  pricePerSession?: string | number | null;
  termStart?: string | null;
  termEnd?: string | null;
  isExpired?: boolean;
  memberships?: ICourseMembershipOption[];
}

/**
 * A single per-option pricing entry for POST /api/courses/:id/renew, keyed by
 * the SOURCE option's membership id. Carries the full pricing structure for the
 * new season's cloned option. Only TIME options send a `prices` tier ladder;
 * entry/calendar options are omitted and carry their pricing forward unchanged.
 */
export interface ICourseRenewOptionPricing {
  sourceMembershipId: number;
  type?: 'calendar' | 'time' | 'entry';
  yearlyPrice?: number;
  prices?: Array<{
    durationDays: number;
    price: number;
    label: string;
    sortOrder: number;
    isDefault: boolean;
  }>;
}

/**
 * Body for POST /api/courses/:id/renew. Adds a NEW season under the same course
 * (the source season stays live — it is not archived). `transferRoster`
 * re-enrolls the source season's currently-active members into the cloned
 * options; `optionPricing` supplies the full per-option pricing for the new
 * season (keyed by SOURCE membership id). Any source option omitted from
 * `optionPricing` carries its full pricing forward unchanged.
 */
export interface ICourseRenewPayload {
  termStart: string;
  termEnd: string;
  transferRoster?: boolean;
  optionPricing?: ICourseRenewOptionPricing[];
}

/**
 * Result of POST /api/courses/:id/renew. Under Model 3 (deferred carry),
 * "prepare next season" only creates the new season row + cloned options (and
 * optionally transfers the roster). Templates carry forward — and the
 * successor's events are generated — at season end via the transition cron, so
 * no template/event counts are returned here.
 */
export interface ICourseRenewResult {
  courseId: number;
  seasonId: number;
  optionsCloned: number;
  membersTransferred: number;
}

/** A season's coarse lifecycle status, derived server-side from term vs today. */
export type SeasonStatus = 'archived' | 'past' | 'current' | 'upcoming';

/** Per-season child counts returned by the seasons-management endpoints. */
export interface ISeasonCounts {
  enrollments: number;
  options: number;
  templates: number;
}

/**
 * A season of a course, as returned by the admin seasons-management endpoints
 * (`GET/PUT /api/courses/:id/seasons[/:seasonId]`, archive/unarchive).
 * `isActive` is the date-driven resolver's pick; `status` is the coarse label.
 * `counts` is present on the list/detail/update responses.
 */
export interface ISeason {
  id: number;
  courseId: number;
  termStart: string | null;
  termEnd: string | null;
  capacity: number | null;
  pricePerSession: string | null;
  enrollmentPolicy: 'open' | 'approval';
  instructorId: number | null;
  instructorName: string | null;
  archivedAt: string | null;
  isActive: boolean;
  status: SeasonStatus;
  counts?: ISeasonCounts;
}

/** Response of GET /api/courses/:id/seasons. */
export interface ISeasonListResponse {
  seasons: ISeason[];
}

/**
 * Response of GET /api/courses/:id/seasons/:seasonId — a single season plus its
 * season-scoped roster + membership options (same option shape as GET /:id).
 */
export interface ISeasonDetail extends ISeason {
  enrollments: unknown[];
  memberships: ICourseMembershipOption[];
}

/**
 * Body for PUT /api/courses/:id/seasons/:seasonId. All fields optional (partial
 * update). `termStart`/`termEnd` are ISO date strings (or null to clear).
 */
export interface ISeasonWritePayload {
  termStart?: string | null;
  termEnd?: string | null;
  capacity?: number | null;
  pricePerSession?: number | null;
  enrollmentPolicy?: 'open' | 'approval';
  instructorId?: number | null;
}

/**
 * Response of PUT /api/courses/:id/seasons/:seasonId — the updated season plus
 * what a term change did to the enrollments hanging off it.
 */
export interface ISeasonUpdateResult extends ISeason {
  /** Deprecated: always 0. Paid enrollments are realigned like every other. */
  skippedPaid: number;
  /** Enrollments whose window was rewritten to the new term. */
  realigned?: number;
  /** Enrollments whose segment could not be resolved — LEFT on the old term. */
  unresolved?: number;
  /** Pending charges moved onto the new window. */
  intentsRealigned?: number;
  /** Pending charges stranded on the old window; the next sync mints a second one. */
  strandedCharges?: number;
  /** True when any of the above needs an admin. Read this, not the counts. */
  needsReview?: boolean;
}

/** Write payload for POST/PUT /api/courses. `termStart`/`termEnd` are new. */
export interface ICourseWritePayload {
  activityId?: number | null;
  name: string;
  description?: string;
  capacity?: number | null;
  enrollmentPolicy?: 'open' | 'approval';
  isActive?: boolean;
  pricePerSession?: number | null;
  instructorId?: number | null;
  termStart?: string | null;
  termEnd?: string | null;
  coverImage?: string | null;
  gallery?: string[] | null;
  extendedDescription?: string | null;
  colorSettings?: { primaryColor?: string; secondaryColor?: string } | null;
}

// Helper to extract data from axios response
export const getData = <T>(response: { data: T }): T => response.data;

// =============================================================================
// ERROR HANDLING UTILITIES
// =============================================================================

/**
 * Get a user-friendly error message from any error
 * Uses the error utilities for consistent error parsing
 * @param error - The error to parse
 * @returns User-friendly error message string
 */
export const getErrorMessage = (error: unknown): string => {
  const parsed = parseError(error);
  return parsed.message;
};

/**
 * Check if an error is a timeout error
 * @param error - The error to check
 * @returns True if the error was caused by a timeout
 */
export const isTimeoutError = (error: unknown): boolean => {
  if (error && typeof error === 'object' && 'code' in error) {
    return (error as { code?: string }).code === 'ECONNABORTED';
  }
  return false;
};

// Re-export error utilities for convenience
export { parseError, isNetworkError, isAuthError } from '../utils/errorUtils';

// =============================================================================
// API ENDPOINTS
// =============================================================================

/** A bank account the organization collects payments into. */
export interface IBankAccount {
  _id: number;
  name: string | null;
  accountNumber: string;
  iban: string;
  /** The org-wide account used by any activity without an override. */
  isDefault: boolean;
}

export interface BankAccountPayload {
  name?: string | null;
  accountNumber: string;
  isDefault?: boolean;
}

export const api = {
  // Entity metadata (schema-driven form generator)
  meta: {
    get: (entity: string) => apiClient.get(`/api/meta/${entity}`),
  },

  // Auth
  auth: {
    login: (email: string, password: string) =>
      apiClient.post<UserInfo>('/api/users/login', { email, password }),

    register: (data: { name: string; email: string; password: string; phoneNumber?: string }) =>
      apiClient.post<UserInfo>('/api/users/register', data),

    getMe: () =>
      apiClient.get<UserInfo>('/api/users/me', { withCredentials: true }),

    logout: () =>
      apiClient.post('/api/users/logout'),

    forgotPassword: (email: string) =>
      apiClient.post('/api/users/forgot-password', { email }),

    resetPassword: (token: string, password: string) =>
      apiClient.post('/api/users/reset-password', { token, password }),

    verifyEmail: (token: string) =>
      apiClient.post('/api/users/verify-email', { token }),

    resendVerification: (email: string) =>
      apiClient.post('/api/users/resend-verification', { email }),

    getPublicOrgInfo: () =>
      apiClient.get<{
        name: string;
        slug: string;
        defaultLanguage: string;
        socialLinks?: { instagram?: string; facebook?: string; website?: string };
      }>('/api/organization/public'),

    switchOrg: (organizationId: string) =>
      apiClient.post<UserInfo>('/api/users/switch-org', { organizationId }),

    /**
     * Rebuild the full session (user + memberships + current org) from the
     * stored token. Used by the cross-subdomain org-switch hand-off so no user
     * data has to travel in the URL.
     */
    session: (token?: string) =>
      apiClient.get<UserInfo>(
        '/api/users/session',
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
      ),
  },

  // User profile
  profile: {
    get: () => apiClient.get<UserInfo>('/api/users/profile'),
    update: (data: Partial<UserInfo>) => apiClient.put('/api/users/profile', data),
    exportData: () => apiClient.get('/api/users/my-data'),
    // GDPR three-operation model (replaces old deleteAccount terminology)
    leaveOrganization: () =>
      apiClient.post<{ message: string; organizationId: string; note: string }>('/api/users/my-org/leave'),
    closeAccount: () =>
      apiClient.delete<{ message: string; closed: { organizations: number; note: string } }>('/api/users/my-account'),
    requestErasure: () =>
      apiClient.post<{ message: string; note: string }>('/api/users/my-account/erasure-request'),
    // Deprecated: use closeAccount() instead
    deleteAccount: () => apiClient.delete('/api/users/my-account'),
  },

  // Organization
  organization: {
    get: () => apiClient.get('/api/organization'),
    update: (data: any) => apiClient.put('/api/organization', data),
    getUsage: () => apiClient.get<OrganizationUsage>('/api/organization/usage'),
    // Self-service spend ceiling: set the member/activity limits the org is
    // willing to pay up to. Returns the fresh usage summary.
    updateLimits: (body: { memberLimit: number; activityLimit: number }) =>
      apiClient.put<OrganizationUsage>('/api/organization/limits', body),
    getSetupStatus: () => apiClient.get('/api/organization/setup-status'),
    // Bank accounts (migration 0044: own table, not the settings blob).
    // Every mutation responds with the full refreshed list.
    bankAccounts: {
      list: () => apiClient.get<IBankAccount[]>('/api/organization/bank-accounts'),
      create: (data: BankAccountPayload) =>
        apiClient.post<IBankAccount[]>('/api/organization/bank-accounts', data),
      update: (id: number, data: BankAccountPayload) =>
        apiClient.put<IBankAccount[]>(`/api/organization/bank-accounts/${id}`, data),
      delete: (id: number) =>
        apiClient.delete<IBankAccount[]>(`/api/organization/bank-accounts/${id}`),
    },
  },

  // Members
  members: {
    /**
     * List members.
     * @param params.filter "unattached" → members with zero activity
     *   enrollments (replaces the legacy external tab).
     */
    list: (params?: { filter?: 'unattached' }) =>
      apiClient.get('/api/members', { params }),
    get: (id: string) => apiClient.get(`/api/members/${id}`),
    create: (data: any) => apiClient.post('/api/members', data),
    update: (id: string, data: any) => apiClient.put(`/api/members/${id}`, data),
    delete: (id: string) => apiClient.delete<MemberDeleteResponse>(`/api/members/${id}`),
    /** Un-deletes a tombstone. Enrollments are NOT resurrected. */
    restore: (id: string | number) =>
      apiClient.post<{ message: string; memberId: number; name?: string }>(`/api/members/${id}/restore`),
    /** Pseudonymizes a member — irreversible erasure of personal data. */
    anonymize: (id: string | number) =>
      apiClient.post<{ message: string; memberId: number; eventId: string | null; applied: boolean; filesQueued: number }>(`/api/members/${id}/anonymize`),
    // Unattached-member management. Historically "external"; the endpoint
    // name is kept for deprecation-window compatibility.
    findDuplicates: () => apiClient.post<any>('/api/members/external/cleanup'),
    merge: (targetId: string, sourceId: string) =>
      apiClient.post(`/api/members/${targetId}/merge`, { sourceId }),
  },

  // Groups
  groups: {
    list: () => apiClient.get('/api/groups'),
    get: (id: string) => apiClient.get(`/api/groups/${id}`),
    create: (data: any) => apiClient.post('/api/groups', data),
    update: (id: string, data: any) => apiClient.put(`/api/groups/${id}`, data),
    delete: (id: string) => apiClient.delete(`/api/groups/${id}`),
    members: (id: string) =>
      apiClient.get<{ groupId: number; kind: GroupSchedulePreview['kind']; members: { _id: number; name: string }[] }>(`/api/groups/${id}/members`),
    schedulePreview: (id: string, action: ScheduleAction, memberIds: (string | number)[]) =>
      apiClient.get<GroupSchedulePreview>(`/api/groups/${id}/members/schedule-preview`, {
        params: { action, memberIds: memberIds.join(',') },
      }),
    // Absent templateIds / eventIds = only the group membership changes.
    addMembers: (id: string, data: { memberIds: number[]; templateIds?: number[]; eventIds?: number[] }) =>
      apiClient.post<GroupMemberChangeResult>(`/api/groups/${id}/members`, data),
    removeMembers: (id: string, data: { memberIds: number[]; templateIds?: number[]; eventIds?: number[] }) =>
      apiClient.delete<GroupMemberChangeResult>(`/api/groups/${id}/members`, { data }),
  },

  // Activities
  activities: {
    list: () => apiClient.get('/api/activities'),
    get: (id: number) => apiClient.get(`/api/activities/${id}`),
    create: (data: any) => apiClient.post('/api/activities', data),
    update: (id: number, data: any) => apiClient.put(`/api/activities/${id}`, data),
    delete: (id: number) => apiClient.delete(`/api/activities/${id}`),
    deleteBlockers: (id: number) => apiClient.get(`/api/activities/${id}/delete-blockers`),
    addMembers: (id: number, data: { memberIds?: number[]; groupId?: number }) =>
      apiClient.post(`/api/activities/${id}/members`, data),
    removeMember: (id: number, memberId: number) =>
      apiClient.delete(`/api/activities/${id}/members/${memberId}`),
    // No memberIds clears every enrollment (used by the blocked-delete dialog).
    removeMembers: (id: number, memberIds?: number[]) =>
      apiClient.delete(`/api/activities/${id}/members`, { data: { memberIds } }),
    // Subject Separation — Phase 2: Activity Creation Wizard endpoints.
    createWithContent: (payload: {
      name: string;
      description?: string;
      coverImage?: string | null;
      visibility?: 'public' | 'internal' | 'hidden';
      joinPolicy?: 'open' | 'approval';
      sortOrder?: number;
      assign?: {
        eventTemplateIds?: number[];
        groupIds?: number[];
        eventIds?: number[];
        memberIds?: number[];
      };
      force?: boolean;
    }) => apiClient.post('/api/activities/with-content', payload),
    getAssignableContent: (params?: { excludeActivityId?: number }) =>
      apiClient.get('/api/activities/assignable-content', { params }),
    listPublic: () => apiClient.get('/api/public/activities'),
    listMy: () => apiClient.get('/api/my/activities'),
    join: (activityId: number, memberId: number) =>
      apiClient.post(`/api/my/activities/${activityId}/join`, { memberId }),
    leave: (activityId: number, memberId: number) =>
      apiClient.post(`/api/my/activities/${activityId}/leave`, { memberId }),
  },

  // Courses (kroužky)
  courses: {
    list: (params?: { activityId?: number }) => apiClient.get('/api/courses', { params }),
    // A course now carries its bounded term: `termStart`/`termEnd` (ISO date or
    // null) and the derived `isExpired` flag (termEnd < today). The GET payload
    // also returns the course's membership OPTIONS via `memberships.course_id`.
    get: (id: number) => apiClient.get<ICourseDetail>(`/api/courses/${id}`),
    create: (data: ICourseWritePayload) => apiClient.post('/api/courses', data),
    update: (id: number, data: Partial<ICourseWritePayload>) => apiClient.put(`/api/courses/${id}`, data),
    delete: (id: number) => apiClient.delete(`/api/courses/${id}`),
    // Prepare the next season: add a NEW season under the same course (the
    // source season stays live). Returns the course id plus what was
    // carried/cloned/transferred.
    renew: (id: number, data: ICourseRenewPayload) =>
      apiClient.post<ICourseRenewResult>(`/api/courses/${id}/renew`, data),
    getSchedule: (id: number) => apiClient.get(`/api/courses/${id}/schedule`),
    // Course membership OPTIONS (memberships whose `courseId` is this course).
    // Managed inline from the course Settings tab; distinct from the retired
    // loose membership↔course links.
    membershipOptions: {
      create: (courseId: number, data: ICourseMembershipOptionPayload) =>
        apiClient.post<ICourseMembershipOption>(`/api/courses/${courseId}/membership-options`, data),
      update: (courseId: number, membershipId: number, data: Partial<ICourseMembershipOptionPayload>) =>
        apiClient.put<ICourseMembershipOption>(
          `/api/courses/${courseId}/membership-options/${membershipId}`,
          data,
        ),
      delete: (courseId: number, membershipId: number) =>
        apiClient.delete(`/api/courses/${courseId}/membership-options/${membershipId}`),
    },
    // Seasons management (admin). A course owns one or more non-overlapping
    // seasons; these expose every season (not just the active one), let an admin
    // edit an arbitrary season, and archive/unarchive it. `update` recomputes
    // enrollment periods when the term changes and reports what it could not
    // carry (`unresolved` / `strandedCharges` / `needsReview`).
    seasons: {
      list: (courseId: number) =>
        apiClient.get<ISeasonListResponse>(`/api/courses/${courseId}/seasons`),
      get: (courseId: number, seasonId: number) =>
        apiClient.get<ISeasonDetail>(`/api/courses/${courseId}/seasons/${seasonId}`),
      update: (courseId: number, seasonId: number, data: ISeasonWritePayload) =>
        apiClient.put<ISeasonUpdateResult>(`/api/courses/${courseId}/seasons/${seasonId}`, data),
      archive: (courseId: number, seasonId: number) =>
        apiClient.post<ISeason>(`/api/courses/${courseId}/seasons/${seasonId}/archive`, {}),
      unarchive: (courseId: number, seasonId: number) =>
        apiClient.post<ISeason>(`/api/courses/${courseId}/seasons/${seasonId}/unarchive`, {}),
      // Season-addressed membership OPTIONS: create/update/delete options on a
      // SPECIFIC season (the flat courses.membershipOptions methods only ever hit
      // the server-resolved active season). The server rejects writes to
      // past/archived seasons with 409 { code: 'SEASON_NOT_EDITABLE' } and a
      // duplicate name with 409 { code: 'MEMBERSHIP_NAME_TAKEN', suggestion }.
      membershipOptions: {
        create: (courseId: number, seasonId: number, data: ICourseMembershipOptionPayload) =>
          apiClient.post<ICourseMembershipOption>(
            `/api/courses/${courseId}/seasons/${seasonId}/membership-options`,
            data,
          ),
        update: (
          courseId: number,
          seasonId: number,
          membershipId: number,
          data: Partial<ICourseMembershipOptionPayload>,
        ) =>
          apiClient.put<ICourseMembershipOption>(
            `/api/courses/${courseId}/seasons/${seasonId}/membership-options/${membershipId}`,
            data,
          ),
        delete: (courseId: number, seasonId: number, membershipId: number) =>
          apiClient.delete(
            `/api/courses/${courseId}/seasons/${seasonId}/membership-options/${membershipId}`,
          ),
        // Offer an EXISTING reusable (season-division) membership in this season
        // by adding the M:N junction row (idempotent). Returns the attached option.
        attach: (courseId: number, seasonId: number, membershipId: number) =>
          apiClient.post<ICourseMembershipOption>(
            `/api/courses/${courseId}/seasons/${seasonId}/membership-options/attach`,
            { membershipId },
          ),
      },
    },
    attachTemplates: (id: number, templateIds: number[]) =>
      apiClient.post(`/api/courses/${id}/templates`, { templateIds }),
    detachTemplate: (id: number, templateId: number) =>
      apiClient.delete(`/api/courses/${id}/templates/${templateId}`),
    attachEvents: (id: number, eventIds: number[]) =>
      apiClient.post(`/api/courses/${id}/events`, { eventIds }),
    detachEvent: (id: number, eventId: number) =>
      apiClient.delete(`/api/courses/${id}/events/${eventId}`),
    join: (courseId: number, memberId: number) =>
      apiClient.post(`/api/my/courses/${courseId}/join`, { memberId }),
    leave: (courseId: number, memberId: number) =>
      apiClient.post<{ status: string; charges: ScheduleCharge[] }>(`/api/my/courses/${courseId}/leave`, { memberId }),
    enrollments: {
      list: (courseId: number) =>
        apiClient.get(`/api/courses/${courseId}/enrollments`),
      // Absent templateIds = every active template of the season; [] = none.
      add: (courseId: number, data: { memberIds: number[]; templateIds?: number[] }) =>
        apiClient.post(`/api/courses/${courseId}/enrollments`, data),
      remove: (courseId: number, memberId: number) =>
        apiClient.delete<{ message: string; schedule: { charges: ScheduleCharge[] } }>(`/api/courses/${courseId}/enrollments/${memberId}`),
      schedulePreview: (courseId: number, action: ScheduleAction, memberIds: number[]) =>
        apiClient.get<SchedulePreview>(`/api/courses/${courseId}/enrollments/schedule-preview`, {
          params: { action, memberIds: memberIds.join(',') },
        }),
      pendingCount: () => apiClient.get('/api/courses/enrollments/pending-count'),
    },
  },

  // Sections (group activities for quick filtering)
  sections: {
    list: () => apiClient.get('/api/sections'),
    get: (id: number) => apiClient.get(`/api/sections/${id}`),
    create: (data: any) => apiClient.post('/api/sections', data),
    update: (id: number, data: any) => apiClient.put(`/api/sections/${id}`, data),
    delete: (id: number) => apiClient.delete(`/api/sections/${id}`),
    listPublic: () => apiClient.get('/api/public/activity-sections'),
    listMy: () => apiClient.get('/api/my/activity-sections'),
  },

  // Events
  events: {
    list: (params?: { start?: string; end?: string }) =>
      apiClient.get('/api/events', { params }),
    get: (id: string) => apiClient.get(`/api/events/${id}`),
    create: (data: any) => apiClient.post('/api/events', data),
    update: (id: string, data: any) => apiClient.put(`/api/events/${id}`, data),
    delete: (id: string) => apiClient.delete(`/api/events/${id}`),
    // Cancel keeps the row AND every charge (0056); the readers stop counting
    // them as owed. Entry credits are returned; attendance is left alone.
    cancel: (id: string) => apiClient.post(`/api/events/${id}/cancel`),
    // What cancelling would do, in numbers, for the confirm dialog.
    cancelPreview: (id: string) =>
      apiClient.get<{
        unpaidCharges: number;
        paidCharges: number;
        entryCreditsToReturn: number;
        affectedMembers: number;
      }>(`/api/events/${id}/cancel-preview`),
    uncancel: (id: string) => apiClient.post(`/api/events/${id}/uncancel`),
    getAttendees: (id: string) => apiClient.get(`/api/events/${id}/attendees`),
    updateAttendance: (id: string, data: any) =>
      apiClient.put(`/api/events/${id}/attendance`, data),
    getCoveragePreview: (id: string) =>
      apiClient.get<{ members: Array<{ memberId: number; method: string; memberMembershipId: number | null; label?: { key: string; params?: Record<string, string | number> } }> }>(
        `/api/events/${id}/coverage-preview`,
      ),
  },

  // Event Templates
  eventTemplates: {
    list: () => apiClient.get('/api/event-templates'),
    get: (id: string) => apiClient.get(`/api/event-templates/${id}`),
    create: (data: any) => apiClient.post('/api/event-templates', data),
    update: (id: string, data: any) => apiClient.put(`/api/event-templates/${id}`, data),
    delete: (id: string) => apiClient.delete<TemplateDeleteResponse>(`/api/event-templates/${id}`),
    generate: (id: string) => apiClient.post(`/api/event-templates/${id}/generate`),
  },

  // Users (admin)
  users: {
    list: () => apiClient.get('/api/users'),
    get: (id: string) => apiClient.get(`/api/users/${id}`),
    create: (data: any) => apiClient.post('/api/users', data),
    update: (id: string, data: any) => apiClient.put(`/api/users/${id}`, data),
    delete: (id: string) => apiClient.delete(`/api/users/${id}`),
  },

  // Roles
  roles: {
    list: () => apiClient.get('/api/roles'),
    get: (id: string) => apiClient.get(`/api/roles/${id}`),
    create: (data: any) => apiClient.post('/api/roles', data),
    update: (id: string, data: any) => apiClient.put(`/api/roles/${id}`, data),
    delete: (id: string) => apiClient.delete(`/api/roles/${id}`),
    getPermissions: () => apiClient.get('/api/roles/permissions'),
    getEntityAccess: (entityType: string, entityId: string | number) =>
      apiClient.get(`/api/roles/entity-access/${entityType}/${entityId}`),
    setEntityAccess: (entityType: string, entityId: string | number, roleIds: number[]) =>
      apiClient.put(`/api/roles/entity-access/${entityType}/${entityId}`, { roleIds }),
    // Subject Separation — Phase 2: activity-scoping for roles.
    getActivityAccess: (roleId: string | number) =>
      apiClient.get<{ activityIds: number[] }>(`/api/roles/${roleId}/activities`),
    setActivityAccess: (roleId: string | number, activityIds: number[]) =>
      apiClient.put<{ activityIds: number[] }>(`/api/roles/${roleId}/activities`, { activityIds }),
  },

  // Payments
  payments: {
    list: (params?: { member?: string; page?: number; limit?: number }) =>
      apiClient.get('/api/payments', { params }),
    get: (id: string) => apiClient.get(`/api/payments/${id}`),
    create: (data: any) => apiClient.post('/api/payments', data),
    update: (id: string, data: any) => apiClient.put(`/api/payments/${id}`, data),
    delete: (id: string) => apiClient.delete(`/api/payments/${id}`),
    getBalances: (params?: { startDate?: string; endDate?: string; groupId?: string; memberId?: string }) =>
      apiClient.get('/api/payments/balances', { params }),
    getQR: (eventId: string, memberId: string, signal?: AbortSignal) =>
      apiClient.get('/api/payments/qr', { params: { eventId, memberId }, responseType: 'blob', signal }),
    getMembershipQR: (membershipId: string, memberId: string, durationDays?: number, signal?: AbortSignal) =>
      apiClient.get('/api/payments/membership-qr', {
        params: { membershipId, memberId, ...(durationDays ? { durationDays } : {}) },
        responseType: 'blob',
        signal,
      }),
    eventQr: (eventId: string) =>
      apiClient.get('/api/payments/event-qr', { params: { eventId }, responseType: 'blob' }),
    getQRDetails: (eventId: string, memberId: string, signal?: AbortSignal) =>
      apiClient.get<PaymentQrDetails>('/api/payments/qr-details', {
        params: { eventId, memberId },
        signal,
      }),
    getMembershipQRDetails: (membershipId: string, memberId: string, signal?: AbortSignal) =>
      apiClient.get<PaymentQrDetails>('/api/payments/membership-qr-details', {
        params: { membershipId, memberId },
        signal,
      }),
    eventQrDetails: (eventId: string, signal?: AbortSignal) =>
      apiClient.get<PaymentQrDetails>('/api/payments/event-qr-details', {
        params: { eventId },
        signal,
      }),
    getOutstandingCount: () =>
      apiClient.get<{ count: number }>('/api/payments/outstanding-count'),
    getOutstanding: (memberId: string) =>
      apiClient.get(`/api/members/${memberId}/outstanding`),
    /**
     * @param bankAccountId Which org account the statement belongs to. Bank
     *   exports name only the counterparty, so it can't be read from the file.
     *   Omitted falls back to the org default account.
     */
    import: (file: File, bankAccountId?: number) => {
      const formData = new FormData();
      formData.append('file', file);
      if (bankAccountId !== undefined) {
        formData.append('bankAccountId', String(bankAccountId));
      }
      return apiClient.post('/api/payments/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    updateIntent: (id: string | number, data: { amount?: number }) =>
      apiClient.put(`/api/payments/intents/${id}`, data).then(r => r.data),
    deleteIntent: (id: string | number) =>
      apiClient.delete(`/api/payments/intents/${id}`).then(r => r.data),
    refund: (id: string | number, data: { amount: number; description?: string }) =>
      apiClient.post(`/api/payments/${id}/refund`, data),
  },

  // Memberships
  memberships: {
    list: () => apiClient.get('/api/memberships'),
    get: (id: string) => apiClient.get(`/api/memberships/${id}`),
    create: (data: any) => apiClient.post('/api/memberships', data),
    update: (id: string, data: any) => apiClient.put(`/api/memberships/${id}`, data),
    delete: (id: string) => apiClient.delete(`/api/memberships/${id}`),
    getMembers: (id: string) => apiClient.get(`/api/memberships/${id}/members`),
    enrollMember: (id: string | number, data: {
      member: number;
      startDate?: string;
      interval?: string;
      durationDays?: number;
      autoRenew?: boolean;
    }) =>
      apiClient.post(`/api/memberships/${id}/members`, data),
    removeMember: (id: string | number, memberId: string | number, action?: 'pause' | 'remove') =>
      apiClient.delete(`/api/memberships/${id}/members/${memberId}${action ? `?action=${action}` : ''}`),
    resumeMember: (id: string | number, memberId: string | number) =>
      apiClient.put(`/api/memberships/${id}/members/${memberId}/resume`).then(r => r.data),
    changeTier: (membershipId: string | number, memberId: string | number, data: { interval?: string; durationDays?: number }) =>
      apiClient.put(`/api/memberships/${membershipId}/members/${memberId}/tier`, data),
  },

  // Join Requests (unified: group / course / activity)
  joinRequests: {
    list: (params?: { status?: string; type?: string }) => apiClient.get('/api/join-requests', { params }),
    pendingCount: () => apiClient.get('/api/join-requests/pending-count'),
    approve: (id: number) => apiClient.put(`/api/join-requests/${id}/approve`),
    deny: (id: number) => apiClient.put(`/api/join-requests/${id}/deny`),
  },

  // Dashboard (user)
  my: {
    // includeAvailable=1 also returns D17 "available" (invited, not on roster) rows.
    events: (params?: { start?: string; end?: string; includeAvailable?: 1 }) =>
      apiClient.get('/api/my/events', { params }),
    members: () => apiClient.get('/api/my/members'),
    getMember: (id: string | number) => apiClient.get(`/api/my/members/${id}`),
    updateMember: (id: string | number, data: any) => apiClient.put(`/api/my/members/${id}`, data),
    groups: () => apiClient.get('/api/my/groups'),
    createMember: (data: any) => apiClient.post('/api/my/members', data),
    // Membership self-service
    memberships: () => apiClient.get('/api/my/memberships'),
    membershipsPendingCount: () =>
      apiClient.get<{ needsChoice: number }>('/api/my/memberships/pending-count'),
    chooseMembership: (
      memberId: string | number,
      data: {
        membershipId: number;
        interval?: string;
        durationDays?: number;
        // Season-division: the season being bought into + the chosen stretch.
        seasonId?: number;
        segmentScheme?: string;
        segmentIndex?: number | null;
      },
    ) => apiClient.post(`/api/my/members/${memberId}/memberships`, data),
    changeMembership: (
      memberId: string | number,
      data: {
        fromMembershipId: number;
        membershipId: number;
        interval?: string;
        durationDays?: number;
        seasonId?: number;
        segmentScheme?: string;
        segmentIndex?: number | null;
      },
    ) => apiClient.post(`/api/my/members/${memberId}/memberships/change`, data),
    cancelMembership: (memberId: string | number, membershipId: string | number) =>
      apiClient.delete(`/api/my/members/${memberId}/memberships/${membershipId}`),
    updateAttendance: (eventId: string, memberId: string, attending: boolean | null, note?: string) =>
      apiClient.put('/api/my/attendance', { eventId, memberId, attending, note }),
    attendance: (params?: { months?: number }) =>
      apiClient.get('/api/my/attendance', { params }),
    outstanding: () => apiClient.get<{ outstanding: any[]; paid: any[] }>('/api/my/outstanding'),
    // --- Member-facing relocations (identical payloads to the admin routes) ---
    // These back the shared admin+member components when used by a member.
    // Admins still use the gated admin namespaces (api.events.../api.payments...).
    eventComments: {
      list: (eventId: string) => apiClient.get(`/api/my/events/${eventId}/comments`),
      create: (eventId: string, body: { content: string; parentCommentId?: string }) =>
        apiClient.post(`/api/my/events/${eventId}/comments`, body),
    },
    // Event requirement DEFINITIONS (what an event asks for). Same payload as the
    // admin GET /api/requirements/events/:eventId/requirements, member-safe route.
    eventRequirements: {
      list: (eventId: string) =>
        apiClient.get(`/api/my/events/${eventId}/requirements`),
    },
    memberRequirements: {
      list: (memberId: string, eventId?: string) =>
        apiClient.get(`/api/my/members/${memberId}/requirements${eventId ? `?event=${eventId}` : ''}`),
      submit: (memberId: string, data: any) =>
        apiClient.post(`/api/my/members/${memberId}/requirements`, data),
      update: (memberId: string, id: string, data: any) =>
        apiClient.put(`/api/my/members/${memberId}/requirements/${id}`, data),
      upload: (memberId: string, formData: FormData) =>
        apiClient.post(`/api/my/members/${memberId}/requirements/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        }),
    },
    payments: {
      qr: (eventId: string, memberId: string, signal?: AbortSignal) =>
        apiClient.get('/api/my/payments/qr', { params: { eventId, memberId }, responseType: 'blob', signal }),
      membershipQr: (membershipId: string, memberId: string, durationDays?: number, signal?: AbortSignal) =>
        apiClient.get('/api/my/payments/membership-qr', {
          params: { membershipId, memberId, ...(durationDays ? { durationDays } : {}) },
          responseType: 'blob',
          signal,
        }),
      qrDetails: (eventId: string, memberId: string, signal?: AbortSignal) =>
        apiClient.get<PaymentQrDetails>('/api/my/payments/qr-details', {
          params: { eventId, memberId },
          signal,
        }),
      membershipQrDetails: (membershipId: string, memberId: string, signal?: AbortSignal) =>
        apiClient.get<PaymentQrDetails>('/api/my/payments/membership-qr-details', {
          params: { membershipId, memberId },
          signal,
        }),
    },
    // Account & privacy actions (GDPR three-operation model)
    account: {
      leaveOrganization: () =>
        apiClient.post('/api/users/my-org/leave'),
      closeAccount: () =>
        apiClient.delete<{ message: string; closed: { organizations: number; note: string } }>('/api/users/my-account'),
      requestErasure: () =>
        apiClient.post('/api/users/my-account/erasure-request'),
    },
  },

  // Impersonation switcher (super admin, or an active impersonation session).
  // Tenant-scoped: the organization comes from the subdomain, never a parameter.
  impersonation: {
    users: () => apiClient.get<ImpersonationUser[]>('/api/impersonation/users'),
    switch: (targetUserId: number) =>
      apiClient.post<ImpersonationSwitchResponse>('/api/impersonation/switch', { targetUserId }),
    stop: () => apiClient.post<{ ok: boolean }>('/api/impersonation/stop', {}),
  },

  // Calendar sync
  calendar: {
    getToken: () => apiClient.get<{ token: string | null; feedUrl: string | null }>('/api/calendar/token'),
    generateToken: () => apiClient.post<{ token: string; feedUrl: string }>('/api/calendar/token', {}),
    deleteToken: () => apiClient.delete('/api/calendar/token'),
    syncEvent: (eventId: number, memberId: number, calendarSync: boolean) =>
      apiClient.put('/api/calendar/event-sync', { eventId, memberId, calendarSync }),
    syncTemplate: (templateId: number, memberId: number, calendarSync: boolean) =>
      apiClient.put('/api/calendar/template-sync', { templateId, memberId, calendarSync }),
    // Admin calendar sync
    getAdminToken: () => apiClient.get<{ token: string | null; feedUrl: string | null }>('/api/calendar/admin/token'),
    generateAdminToken: () => apiClient.post<{ token: string; feedUrl: string }>('/api/calendar/admin/token', {}),
    deleteAdminToken: () => apiClient.delete('/api/calendar/admin/token'),
  },

  // Notifications
  notifications: {
    list: () => apiClient.get('/api/notifications'),
    getCounts: () => apiClient.get('/api/notifications/unread-counts'),
    markRead: (id: string) => apiClient.put(`/api/notifications/${id}/read`),
    markAllRead: () => apiClient.put('/api/notifications/read-all'),
    // Notification type catalog — drives the preferences matrix so it cannot
    // drift from the backend registry.
    catalog: () => apiClient.get('/api/notifications/catalog'),
    registerDeviceToken: (token: string, platform: 'web' | 'ios' | 'android') =>
      apiClient.post('/api/notifications/device-token', { token, platform }),
    // `authToken` is for logout, which clears localStorage synchronously before
    // the axios interceptor runs; an explicitly set header takes precedence.
    removeDeviceToken: (token: string, authToken?: string) =>
      apiClient.delete('/api/notifications/device-token', {
        data: { token },
        ...(authToken ? { headers: { Authorization: `Bearer ${authToken}` } } : {}),
      }),
  },

  // Announcements
  announcements: {
    list: (params?: { limit?: number; skip?: number }) =>
      apiClient.get('/api/announcements', { params }),
    my: (params?: { limit?: number; skip?: number }) =>
      apiClient.get('/api/announcements/my', { params }),
    forEvent: (eventId: string) =>
      apiClient.get(`/api/announcements/event/${eventId}`),
    create: (data: { message: string; events: (string | number)[]; groups: (string | number)[] }) =>
      apiClient.post('/api/announcements', data),
    delete: (id: string) => apiClient.delete(`/api/announcements/${id}`),
  },

  // Messages
  messages: {
    getConversations: () => apiClient.get('/api/messages/conversations'),
    getMessages: (userId: string) => apiClient.get(`/api/messages/${userId}`),
    send: (userId: string, content: string) =>
      apiClient.post(`/api/messages/${userId}`, { content }),
    markRead: (conversationId: string) =>
      apiClient.put(`/api/messages/${conversationId}/read`),
  },

  // Communication Center — unified inbox for event-comment threads and DMs.
  communication: {
    threads: (params?: CommThreadsParams) =>
      apiClient.get<{ threads: CommThread[]; hasMore: boolean }>(
        '/api/communication/threads',
        { params }
      ),
    markRead: (type: 'event' | 'dm', refId: number) =>
      apiClient.put(`/api/communication/threads/${type}/${refId}/read`),
  },

  // Statistics
  statistics: {
    get: () => apiClient.get('/api/statistics'),
    getAttendance: (params?: { startDate?: string; endDate?: string; groupId?: string; memberId?: string }) =>
      apiClient.get('/api/statistics/attendance', { params }),
    getAttendanceBadges: () =>
      apiClient.get('/api/statistics/attendance/badges'),
  },

  // Public events
  publicEvents: {
    list: () => apiClient.get('/api/public/events'),
    get: (slug: string) => apiClient.get(`/api/public/events/${slug}`),
    register: (slug: string, data: any) =>
      apiClient.post(`/api/public/events/${slug}/register`, data),
  },

  // Requirements
  requirements: {
    // Requirement Types
    types: {
      list: () => apiClient.get('/api/requirements/types'),
      create: (data: any) => apiClient.post('/api/requirements/types', data),
      update: (id: string, data: any) => apiClient.put(`/api/requirements/types/${id}`, data),
      delete: (id: string) => apiClient.delete(`/api/requirements/types/${id}`),
    },
    // Event Requirements
    eventRequirements: {
      list: (eventId: string) => apiClient.get(`/api/requirements/events/${eventId}/requirements`),
      create: (eventId: string, data: any) =>
        apiClient.post(`/api/requirements/events/${eventId}/requirements`, data),
      update: (eventId: string, id: string, data: any) =>
        apiClient.put(`/api/requirements/events/${eventId}/requirements/${id}`, data),
      delete: (eventId: string, id: string) =>
        apiClient.delete(`/api/requirements/events/${eventId}/requirements/${id}`),
    },
    // Member Requirements
    memberRequirements: {
      list: (memberId: string, eventId?: string) =>
        apiClient.get(`/api/requirements/members/${memberId}/requirements${eventId ? `?event=${eventId}` : ''}`),
      submit: (memberId: string, data: any) =>
        apiClient.post(`/api/requirements/members/${memberId}/requirements`, data),
      update: (memberId: string, id: string, data: any) =>
        apiClient.put(`/api/requirements/members/${memberId}/requirements/${id}`, data),
    },
  },

  // Uploads — unified registry-backed endpoint (POST/DELETE /api/uploads/:kind).
  // All kinds use the same 'file' form field. Kind-specific wrappers below
  // preserve the original method names for existing callers.
  uploads: {
    /**
     * Upload a file for the given kind. Optional context (e.g. activityId)
     * is sent as a query string so the backend pre-check / persistTo can read it.
     */
    upload: (
      kind:
        | 'staff-photo'
        | 'logo'
        | 'favicon'
        | 'welcome-banner'
        | 'welcome-background'
        | 'gallery'
        | 'activity-cover'
        | 'requirement-template'
        | 'member-submission',
      file: File,
      context?: Record<string, string | number>
    ) => {
      const formData = new FormData();
      formData.append('file', file);
      const qs = context
        ? `?${new URLSearchParams(
            Object.fromEntries(
              Object.entries(context).map(([k, v]) => [k, String(v)])
            )
          ).toString()}`
        : '';
      return apiClient.post<{ message: string; url: string; filename: string; size: number }>(
        `/api/uploads/${kind}${qs}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
    },
    /**
     * Delete an upload. For unique-scheme kinds (gallery, activity-cover, etc.)
     * pass `url` of the file to remove; for stable-scheme kinds (logo, favicon,
     * welcome-banner) omit `url`.
     */
    deleteUpload: (
      kind:
        | 'staff-photo'
        | 'logo'
        | 'favicon'
        | 'welcome-banner'
        | 'welcome-background'
        | 'gallery'
        | 'activity-cover'
        | 'requirement-template'
        | 'member-submission',
      url?: string
    ) => {
      const qs = url ? `?url=${encodeURIComponent(url)}` : '';
      return apiClient.delete(`/api/uploads/${kind}${qs}`);
    },

    /**
     * Download a member's submitted requirement file.
     *
     * Submissions are no longer served from the public /uploads static mount --
     * the bytes come through an authenticated route keyed by the requirement
     * row, so this has to be a bearer-authenticated blob GET rather than a
     * plain <a href> to the stored URL.
     */
    downloadSubmission: (memberId: string, requirementId: string, isUserView = false) =>
      apiClient.get(
        isUserView
          ? `/api/my/members/${memberId}/requirements/${requirementId}/file`
          : `/api/requirements/members/${memberId}/requirements/${requirementId}/file`,
        { responseType: 'blob' }
      ),

    // Back-compat helpers (same signatures as before).
    galleryImage: (file: File) => api.uploads.upload('gallery', file),
    deleteGalleryImage: (url: string) => api.uploads.deleteUpload('gallery', url),
    logo: (file: File) => api.uploads.upload('logo', file),
    deleteLogo: () => api.uploads.deleteUpload('logo'),
    favicon: (file: File) => api.uploads.upload('favicon', file),
    deleteFavicon: () => api.uploads.deleteUpload('favicon'),
    welcomeBanner: (file: File) => api.uploads.upload('welcome-banner', file),
    deleteWelcomeBanner: () => api.uploads.deleteUpload('welcome-banner'),
    welcomeBackground: (file: File) => api.uploads.upload('welcome-background', file),
    deleteWelcomeBackground: () => api.uploads.deleteUpload('welcome-background'),

    // New kind: activity cover image (linked to an existing activity by ID).
    activityCover: (activityId: number, file: File) =>
      api.uploads.upload('activity-cover', file, { activityId }),
  },

  // Billing
  billing: {
    getSubscription: () =>
      apiClient.get<{
        plan: string;
        status: string;
        billingInterval: string | null;
        cancelAtPeriodEnd: boolean;
        currentPeriodEnd: string | null;
        expiresAt: string | null;
        isDemo: boolean;
        billingExempt: boolean;
        hasStripeSubscription: boolean;
        hasStripeCustomer: boolean;
        stripeConfigured: boolean;
      }>('/api/billing/subscription'),

    createCheckoutSession: (data: {
      plan: string;
      interval: 'monthly' | 'yearly';
      successUrl: string;
      cancelUrl: string;
    }) =>
      apiClient.post<{ url: string }>('/api/billing/create-checkout-session', data),

    createPortalSession: (data: { returnUrl: string }) =>
      apiClient.post<{ url: string }>('/api/billing/create-portal-session', data),

    /** This org's own invoicing details, plus which required fields are missing. */
    getDetails: () =>
      apiClient.get<{
        billingDetails: BillingDetailsPayload | null;
        missingFields: string[];
        requiredFields: string[];
      }>('/api/billing/details'),

    updateDetails: (data: BillingDetailsPayload) =>
      apiClient.put<{ billingDetails: BillingDetailsPayload; missingFields: string[] }>(
        '/api/billing/details',
        data,
      ),

    getInvoices: (params?: { page?: number; limit?: number }) =>
      apiClient.get<{
        invoices: Array<{
          _id: string;
          invoiceNumber: string | null;
          periodStart: string;
          periodEnd: string;
          currency: string;
          netAmount: number;
          vatAmount: number;
          grossAmount: number;
          status: 'draft' | 'issued' | 'paid' | 'overdue' | 'cancelled';
          issuedAt: string | null;
          dueDate: string | null;
          paidAt: string | null;
        }>;
        pagination: { page: number; limit: number; total: number; pages: number };
      }>('/api/billing/invoices', { params }),

    downloadInvoicePdf: (invoiceId: string) =>
      apiClient.get(`/api/billing/invoices/${invoiceId}/pdf`, { responseType: 'blob' }),
  },

  // Public endpoints (no auth required)
  public: {
    // Contact form
    submitContact: (data: { name: string; email: string; phone?: string; company?: string; message: string }) =>
      apiClient.post<{ message: string; submissionId: string }>('/api/public/contact', data),

    // Pricing — usage-based model: a single base plan plus add-on blocks.
    getPricing: () =>
      apiClient.get<PublicPricing>('/api/public/pricing'),

    // Demo
    requestDemo: (email: string) =>
      apiClient.post<{ message: string }>('/api/public/demo/request', { email }),

    verifyDemo: (token: string) =>
      apiClient.get<{ message: string; valid: boolean; email: string; token: string }>(`/api/public/demo/verify/${token}`),

    createDemo: (data: { token: string; orgName: string; password: string; userName?: string }) =>
      apiClient.post<{ message: string; slug: string; email: string; demoEndsAt: string }>('/api/public/demo/create', data),
  },

  // Super Admin endpoints
  superAdmin: {
    // Dashboard
    getDashboard: () => apiClient.get<any>('/api/super-admin/dashboard'),
    auditLogs: {
      list: (params: { category?: string; search?: string; page?: number; limit?: number }) =>
        apiClient.get<{ logs: any[]; pagination: { total: number; page: number; pages: number } }>(
          '/api/super-admin/audit-logs',
          { params },
        ),
    },

    // Organizations
    organizations: {
      list: () => apiClient.get<{ organizations: any[]; pagination: any }>('/api/super-admin/organizations'),
      get: (id: string) => apiClient.get<any>(`/api/super-admin/organizations/${id}`),
      create: (data: any) => apiClient.post<any>('/api/super-admin/organizations', data),
      update: (id: string, data: any) => apiClient.put<any>(`/api/super-admin/organizations/${id}`, data),
      delete: (id: string) => apiClient.delete(`/api/super-admin/organizations/${id}`),
      getUsers: (id: string) => apiClient.get<any[]>(`/api/super-admin/organizations/${id}/users`),
      // Export/Import endpoints
      export: (id: string) => apiClient.post(`/api/super-admin/organizations/${id}/export`, {}, { responseType: 'blob' }),
      import: (data: any) => apiClient.post<any>('/api/super-admin/organizations/import', data),
      validateImport: (data: any) => apiClient.post<any>('/api/super-admin/organizations/import/validate', data),
      archiveDelete: (id: string) => apiClient.delete(`/api/super-admin/organizations/${id}/archive-delete?archive=true`, { responseType: 'blob' }),

      // Platform billing (organization → Eventik)
      getInvoices: (id: string, params?: { status?: string; page?: number; limit?: number }) =>
        apiClient.get<{ invoices: any[]; pagination: any }>(`/api/super-admin/organizations/${id}/invoices`, { params }),
      getBillingPreview: (id: string) =>
        apiClient.get<any>(`/api/super-admin/organizations/${id}/billing-preview`),
      getBillingDetails: (id: string) =>
        apiClient.get<{ billingDetails: any | null }>(`/api/super-admin/organizations/${id}/billing-details`),
      updateBillingDetails: (id: string, data: any) =>
        apiClient.put<{ billingDetails: any }>(`/api/super-admin/organizations/${id}/billing-details`, data),
      getBillingPeriods: (id: string) =>
        apiClient.get<any>(`/api/super-admin/organizations/${id}/billing-periods`),
      createBillingPeriod: (id: string, data: { periodStart: string; peakMembers?: number; peakActivities?: number }) =>
        apiClient.post<{ invoice: any; created: boolean }>(`/api/super-admin/organizations/${id}/billing-periods`, data),
      setBillingExempt: (id: string, exempt: boolean) =>
        apiClient.put<{ exempt: boolean }>(`/api/super-admin/organizations/${id}/billing-exempt`, { exempt }),
    },

    // Platform invoices
    invoices: {
      get: (invoiceId: string) => apiClient.get<any>(`/api/super-admin/invoices/${invoiceId}`),
      downloadPdf: (invoiceId: string) =>
        apiClient.get(`/api/super-admin/invoices/${invoiceId}/pdf`, { responseType: 'blob' }),
      markPaid: (invoiceId: string) => apiClient.post<any>(`/api/super-admin/invoices/${invoiceId}/mark-paid`),
      cancel: (invoiceId: string) => apiClient.post<any>(`/api/super-admin/invoices/${invoiceId}/cancel`),
      resendEmail: (invoiceId: string) => apiClient.post<{ emailed: boolean }>(`/api/super-admin/invoices/${invoiceId}/resend-email`),
      issue: (invoiceId: string, email: boolean) =>
        apiClient.post<{ invoice: any; emailed: boolean }>(`/api/super-admin/invoices/${invoiceId}/issue${email ? '?email=true' : ''}`),
      updatePeaks: (invoiceId: string, data: { peakMembers: number; peakActivities: number }) =>
        apiClient.put<any>(`/api/super-admin/invoices/${invoiceId}/peaks`, data),
    },

    // Users (cross-organization)
    users: {
      list: (params?: { page?: number; limit?: number; search?: string; organizationId?: string }) =>
        apiClient.get<{ users: any[]; pagination: any }>('/api/super-admin/users', { params }),
      get: (id: string) => apiClient.get<any>(`/api/super-admin/users/${id}`),
      update: (id: string, data: any) => apiClient.put<any>(`/api/super-admin/users/${id}`, data),
      delete: (id: string) => apiClient.delete(`/api/super-admin/users/${id}`),
      impersonate: (id: string) => apiClient.post<UserInfo>(`/api/super-admin/users/${id}/impersonate`),
    },

    // Contact submissions
    contactSubmissions: {
      list: (params?: { page?: number; limit?: number; status?: string }) =>
        apiClient.get<{ submissions: any[]; pagination: any }>('/api/super-admin/contact-submissions', { params }),
      get: (id: string) => apiClient.get<any>(`/api/super-admin/contact-submissions/${id}`),
      update: (id: string, data: { status?: string; notes?: string }) =>
        apiClient.put<any>(`/api/super-admin/contact-submissions/${id}`, data),
      delete: (id: string) => apiClient.delete(`/api/super-admin/contact-submissions/${id}`),
    },

    // Demo organizations
    demoOrgs: {
      create: (data: {
        prospectName: string;
        slug?: string;
        profile?: string;
        ownerEmail?: string;
        expiresInDays?: number;
      }) =>
        apiClient.post<{
          organization: { _id: number; name: string; slug: string; demoExpiresAt: string };
          owner: { email: string; password: string };
          profile: { key: string; label: string };
          counts: Record<string, number>;
        // Schema build + seed outlasts the 30s default; a timeout here loses
        // the one-time owner password even though the demo was created.
        }>('/api/super-admin/demo-orgs', data, { timeout: 180000 }),
      remove: (id: string) => apiClient.delete(`/api/super-admin/demo-orgs/${id}`),
      list: (params?: { page?: number; limit?: number; status?: string }) =>
        apiClient.get<{ demoOrgs: any[]; pagination: any }>('/api/super-admin/demo-orgs', { params }),
      extend: (id: string, days?: number) =>
        apiClient.post<{ message: string; organization: any; newExpiresAt: string; newDeletionAt: string }>(
          `/api/super-admin/demo-orgs/${id}/extend`,
          { days }
        ),
    },
  },
};

export default api;
